/**
 * Error metrics tracking and monitoring
 * Tracks error rates, patterns, and health scores in-memory with sliding window
 */

interface ErrorMetric {
  timestamp: number;
  errorType: string;
  queryKey: string;
  statusCode?: number;
}

interface ErrorSummary {
  count: number;
  lastOccurrence: number;
  queryKey: string;
  errorType: string;
  statusCode?: number;
}

interface ErrorMetrics {
  totalErrors: number;
  errorRate: number; // errors per minute
  topErrors: ErrorSummary[];
  criticalErrors: ErrorSummary[];
  healthScore: number; // 0-100
  timeWindow: string;
}

class ErrorMetricsTracker {
  private errors: ErrorMetric[] = [];
  private readonly WINDOW_SIZE = 60 * 60 * 1000; // 1 hour
  private readonly CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Start cleanup timer only on client side
    if (typeof window !== 'undefined') {
      this.startCleanup();
    }
  }

  /**
   * Record a new error
   */
  recordError(
    errorType: string,
    queryKey: string,
    statusCode?: number
  ): void {
    const metric: ErrorMetric = {
      timestamp: Date.now(),
      errorType,
      queryKey,
      statusCode,
    };

    this.errors.push(metric);
    this.cleanup();
  }

  /**
   * Get current error metrics
   */
  getMetrics(): ErrorMetrics {
    this.cleanup();

    const now = Date.now();
    const windowStart = now - this.WINDOW_SIZE;
    const recentErrors = this.errors.filter(e => e.timestamp >= windowStart);

    // Calculate error rate (errors per minute)
    const minutesInWindow = this.WINDOW_SIZE / (60 * 1000);
    const errorRate = recentErrors.length / minutesInWindow;

    // Group errors by query key and type
    const errorGroups = new Map<string, ErrorSummary>();
    
    recentErrors.forEach(error => {
      const key = `${error.queryKey}:${error.errorType}`;
      const existing = errorGroups.get(key);
      
      if (existing) {
        existing.count++;
        existing.lastOccurrence = Math.max(existing.lastOccurrence, error.timestamp);
      } else {
        errorGroups.set(key, {
          count: 1,
          lastOccurrence: error.timestamp,
          queryKey: error.queryKey,
          errorType: error.errorType,
          statusCode: error.statusCode,
        });
      }
    });

    // Sort by count to get top errors
    const sortedErrors = Array.from(errorGroups.values())
      .sort((a, b) => b.count - a.count);

    // Identify critical errors (server errors, high frequency)
    const criticalErrors = sortedErrors.filter(e => 
      e.errorType === 'server' || 
      e.errorType === 'authorization' ||
      e.count > 10
    );

    // Calculate health score (100 = perfect, 0 = critical)
    const healthScore = this.calculateHealthScore(
      recentErrors.length,
      errorRate,
      criticalErrors.length
    );

    return {
      totalErrors: recentErrors.length,
      errorRate: Math.round(errorRate * 100) / 100,
      topErrors: sortedErrors.slice(0, 10),
      criticalErrors,
      healthScore,
      timeWindow: '1 hour',
    };
  }

  /**
   * Calculate system health score based on error patterns
   */
  private calculateHealthScore(
    totalErrors: number,
    errorRate: number,
    criticalErrorCount: number
  ): number {
    let score = 100;

    // Deduct points for total errors
    if (totalErrors > 100) score -= 30;
    else if (totalErrors > 50) score -= 20;
    else if (totalErrors > 20) score -= 10;
    else if (totalErrors > 10) score -= 5;

    // Deduct points for error rate
    if (errorRate > 10) score -= 30; // >10 errors/minute is critical
    else if (errorRate > 5) score -= 20;
    else if (errorRate > 2) score -= 10;
    else if (errorRate > 1) score -= 5;

    // Deduct points for critical errors
    if (criticalErrorCount > 10) score -= 40;
    else if (criticalErrorCount > 5) score -= 25;
    else if (criticalErrorCount > 2) score -= 15;
    else if (criticalErrorCount > 0) score -= 10;

    return Math.max(0, Math.min(100, score));
  }

  /**
   * Check if error rate exceeds threshold
   */
  shouldAlert(): {
    alert: boolean;
    level: 'warning' | 'critical' | 'none';
    reason?: string;
  } {
    const metrics = this.getMetrics();

    // Critical: >50 errors/min or health score <30
    if (metrics.errorRate > 50 || metrics.healthScore < 30) {
      return {
        alert: true,
        level: 'critical',
        reason: `Critical: ${metrics.errorRate.toFixed(1)} errors/min, health score: ${metrics.healthScore}`,
      };
    }

    // Warning: >10 errors/min or health score <60
    if (metrics.errorRate > 10 || metrics.healthScore < 60) {
      return {
        alert: true,
        level: 'warning',
        reason: `Warning: ${metrics.errorRate.toFixed(1)} errors/min, health score: ${metrics.healthScore}`,
      };
    }

    return { alert: false, level: 'none' };
  }

  /**
   * Remove old errors outside the time window
   */
  private cleanup(): void {
    const cutoff = Date.now() - this.WINDOW_SIZE;
    this.errors = this.errors.filter(e => e.timestamp >= cutoff);
  }

  /**
   * Start periodic cleanup
   */
  private startCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Clear all metrics (for testing)
   */
  clear(): void {
    this.errors = [];
  }

  /**
   * Stop cleanup timer
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }
}

// Singleton instance
export const errorMetricsTracker = new ErrorMetricsTracker();

/**
 * Get current error metrics
 */
export function getErrorMetrics(): ErrorMetrics {
  return errorMetricsTracker.getMetrics();
}

/**
 * Record a query error for metrics
 */
export function recordQueryError(
  errorType: string,
  queryKey: string,
  statusCode?: number
): void {
  errorMetricsTracker.recordError(errorType, queryKey, statusCode);
}

/**
 * Check if alerts should be triggered
 */
export function checkErrorAlerts(): {
  alert: boolean;
  level: 'warning' | 'critical' | 'none';
  reason?: string;
} {
  return errorMetricsTracker.shouldAlert();
}

/**
 * Format metrics for display
 */
export function formatMetricsForDisplay(metrics: ErrorMetrics): string {
  return `
Error Metrics (${metrics.timeWindow}):
- Total Errors: ${metrics.totalErrors}
- Error Rate: ${metrics.errorRate}/min
- Health Score: ${metrics.healthScore}/100
- Critical Errors: ${metrics.criticalErrors.length}
- Top Error: ${metrics.topErrors[0]?.queryKey || 'None'} (${metrics.topErrors[0]?.count || 0}x)
  `.trim();
}
